import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Breadcrumb,
  Tag,
  Card,
  Row,
  Col,
  message,
  Loading,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toolsService, Tool, ToolType } from '@/renderer/services/toolsService'
import './ToolsList.css'

const TOOL_STATUSES = [
  { label: 'Available (AVLB)', value: 'AVLB', color: 'green' },
  { label: 'In Use (NUSE)', value: 'NUSE', color: 'blue' },
  { label: 'Inspection (INSP)', value: 'INSP', color: 'orange' },
  { label: 'Maintenance (MTCE)', value: 'MTCE', color: 'orange' },
  { label: 'Repair (REPR)', value: 'REPR', color: 'red' },
  { label: 'Quarantined (QRTD)', value: 'QRTD', color: 'red' },
  { label: 'Retired (RTRD)', value: 'RTRD', color: 'purple' },
]

const COMPANY_LOCATIONS = [
  { label: 'Neuquén - Operaciones', value: 'Neuquén - Operaciones' },
  { label: 'Neuquén - Base', value: 'Neuquén - Base' },
  { label: 'Comodoro - Operaciones', value: 'Comodoro - Operaciones' },
  { label: 'Comodoro - Base', value: 'Comodoro - Base' },
  { label: 'Buenos Aires - Office', value: 'Buenos Aires - Office' },
]

interface ExtendedTool extends Tool {
  key: string
}

export const ToolsList: React.FC = () => {
  const [tools, setTools] = useState<ExtendedTool[]>([])
  const [toolTypes, setToolTypes] = useState<ToolType[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingTool, setEditingTool] = useState<Tool | null>(null)
  const [form] = Form.useForm()
  const [filterStatus, setFilterStatus] = useState<string>()

  // Load initial data
  useEffect(() => {
    loadToolTypes()
    loadTools()
  }, [])

  const loadToolTypes = async () => {
    try {
      const data = await toolsService.listToolTypes(1, 500)
      setToolTypes(data.items)
    } catch (error) {
      message.error('Failed to load tool types')
      console.error(error)
    }
  }

  const loadTools = async (page: number = 1, status?: string) => {
    setLoading(true)
    try {
      const data = await toolsService.listTools(page, pagination.pageSize, status)
      const toolsWithKey = data.items.map((tool) => ({
        ...tool,
        key: tool.id,
      }))
      setTools(toolsWithKey)
      setPagination({
        current: page,
        pageSize: pagination.pageSize,
        total: data.total,
      })
    } catch (error) {
      message.error('Failed to load tools')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddTool = () => {
    setEditingTool(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEditTool = (tool: Tool) => {
    setEditingTool(tool)
    form.setFieldsValue({
      serial_number: tool.serial_number,
      tool_type_id: tool.tool_type_id,
      location: tool.location,
      status: tool.status,
      notes: tool.description,
      internal_id: tool.internal_id,
      sap_id: tool.sap_id,
    })
    setIsModalVisible(true)
  }

  const handleDeleteTool = async (toolId: string) => {
    try {
      await toolsService.deleteTool(toolId)
      message.success('Tool deleted successfully')
      loadTools(pagination.current)
    } catch (error) {
      message.error('Failed to delete tool')
      console.error(error)
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      if (editingTool) {
        await toolsService.updateTool(editingTool.id, values)
        message.success('Tool updated successfully')
      } else {
        await toolsService.createTool(values)
        message.success('Tool created successfully')
      }

      setIsModalVisible(false)
      loadTools(pagination.current)
    } catch (error) {
      console.error('Form validation failed:', error)
      message.error('Failed to save tool')
    } finally {
      setLoading(false)
    }
  }

  const columns: ColumnsType<ExtendedTool> = [
    {
      title: 'Serial Number',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 150,
      sorter: (a, b) => a.serial_number.localeCompare(b.serial_number),
    },
    {
      title: 'Type',
      dataIndex: ['tool_type', 'name'],
      key: 'tool_type_name',
      width: 150,
      render: (_, record) => record.tool_type?.name || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = TOOL_STATUSES.find((s) => s.value === status)
        return statusConfig ? <Tag color={statusConfig.color}>{statusConfig.label}</Tag> : status
      },
      filters: TOOL_STATUSES.map((s) => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => {
        setFilterStatus(value as string)
        loadTools(1, value as string)
        return true
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 120,
      render: (location) => location || '-',
    },
    {
      title: 'SAP ID',
      dataIndex: 'sap_id',
      key: 'sap_id',
      width: 100,
      render: (sap_id) => sap_id || '-',
    },
    {
      title: 'Usage (Runs/Hours/Feet)',
      key: 'usage',
      width: 180,
      render: (_, record) => (
        <span>
          {record.total_runs}/{record.total_hours}/{record.total_feet}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditTool(record)}
            title="Edit tool"
          />
          <Popconfirm
            title="Delete Tool"
            description="Are you sure you want to delete this tool?"
            onConfirm={() => handleDeleteTool(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} title="Delete tool" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="tools-list-container">
      <Breadcrumb items={[{ title: 'Inventory' }, { title: 'Tools' }]} />

      <Card
        title="Tools Inventory"
        extra={
          <Space>
            <Button icon={<UploadOutlined />}>Import Excel</Button>
            <Button icon={<DownloadOutlined />}>Export Excel</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTool}>
              New Tool
            </Button>
          </Space>
        }
        style={{ marginTop: '16px' }}
      >
        <Table
          columns={columns}
          dataSource={tools}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['50', '100', '200'],
            onChange: (page) => loadTools(page, filterStatus),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingTool ? 'Edit Tool' : 'New Tool'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        loading={loading}
      >
        <Form form={form} layout="vertical" style={{ marginTop: '16px' }}>
          <Form.Item
            label="Serial Number"
            name="serial_number"
            rules={[{ required: true, message: 'Serial number is required' }]}
          >
            <Input placeholder="Enter serial number" />
          </Form.Item>

          <Form.Item
            label="Tool Type"
            name="tool_type_id"
            rules={[{ required: true, message: 'Tool type is required' }]}
          >
            <Select
              placeholder="Select tool type"
              options={toolTypes.map((t) => ({ label: t.name, value: t.id }))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Internal ID" name="internal_id">
                <Input placeholder="Internal ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="SAP ID" name="sap_id">
                <Input placeholder="SAP ID" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Status" name="status">
                <Select
                  placeholder="Select status"
                  defaultValue="AVLB"
                  options={TOOL_STATUSES.map((s) => ({ label: s.label, value: s.value }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Location" name="location" rules={[{ required: true, message: 'Location is required' }]}>
                <Select
                  placeholder="Select location"
                  options={COMPANY_LOCATIONS}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description/Notes" name="notes">
            <Input.TextArea placeholder="Notes" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ToolsList
